import { useEffect, useMemo, useState } from 'react'
import { useStore } from '@/store/useStore'
import { useShallow } from 'zustand/react/shallow'
import { createLearningReview } from '@/entities/assessment'
import type { GeneratedLearningPlan } from '@/types'
import { storageHealth } from '@/services/storage-health'
import { useStorageHealth } from '@/services/storage-health/useStorageHealth'
import type { ExerciseAttempt } from '@/entities/attempt'

type StorePersistenceApi = {
  hasHydrated: () => boolean
  onFinishHydration: (listener: () => void) => () => void
  rehydrate: () => Promise<void> | void
}

export function useStoreHydrationState() {
  const persistence = (useStore as typeof useStore & { persist?: StorePersistenceApi }).persist
  const [hydrated, setHydrated] = useState(() => persistence?.hasHydrated() ?? true)
  const health = useStorageHealth()
  useEffect(() => persistence?.onFinishHydration(() => setHydrated(true)), [persistence])
  const retry = async () => {
    storageHealth.clear()
    setHydrated(false)
    await persistence?.rehydrate()
  }
  return {
    status: hydrated ? 'ready' as const : health.status === 'error' ? 'failed' as const : 'hydrating' as const,
    retry,
  }
}

export function useStoreHydration() {
  return useStoreHydrationState().status === 'ready'
}

export const useStudentDirectory = () => useStore(useShallow((state) => ({
  students: state.studentIndex,
  activeStudentId: state.activeStudentId,
  workspaces: state.workspaces,
  createStudent: state.createStudent,
  createStudentAndCommit: state.createStudentAndCommit,
  selectStudent: state.selectStudent,
  deleteStudent: state.deleteStudent,
  replaceStudentData: state.replaceStudentData,
})))

export const useLearningPlanState = () => useStore(useShallow((state) => ({
  currentPlan: state.currentPlan,
  currentPlanKey: state.currentPlanKey,
  setCurrentPlan: state.setCurrentPlan,
  clearCurrentPlan: state.clearCurrentPlan,
})))

export const useLearningReportState = () => useStore(useShallow((state) => ({
  attempts: state.attempts,
  currentReport: state.currentReport,
  learningEvents: state.learningEvents,
})))

export const useDashboardState = () => useStore(useShallow((state) => ({
  stars: state.stars,
  game: state.game,
  completedTasks: state.completedTasks,
  taskCompletedAt: state.taskCompletedAt,
  getDailyQuest: state.getDailyQuest,
  initializeDailyQuest: state.initializeDailyQuest,
  learnerType: state.learnerType,
  setLearnerType: state.setLearnerType,
  weeklyPlanActive: state.weeklyPlanActive,
  weeklyTaskDone: state.weeklyTaskDone,
  wrongQuestions: state.wrongQuestions,
  wrongQuestionCount: state.wrongQuestions.filter((question) => (
    (question.masteryState ?? question.status) !== 'mastered'
  )).length,
})))

export const useProgressState = () => useStore(useShallow((state) => ({
  stars: state.stars,
  completedTasks: state.completedTasks,
  taskCompletedAt: state.taskCompletedAt,
  currentReport: state.currentReport,
  completeLearningRouteTaskAndCommit: state.completeLearningRouteTaskAndCommit,
})))

export const useAssessmentActions = () => useStore(useShallow((state) => ({
  getEffectiveLayer: state.getEffectiveLayer,
  assessmentRecords: state.assessmentRecords,
  assessmentSessions: state.assessmentSessions,
  startGraduationAssessmentSession: state.startGraduationAssessmentSession,
  submitGraduationAssessmentResponse: state.submitGraduationAssessmentResponse,
  completeGraduationAssessmentSessionAndCommit: state.completeGraduationAssessmentSessionAndCommit,
  recordDiagnosticAssessmentAnswerAndCommit: state.recordDiagnosticAssessmentAnswerAndCommit,
  completeTask: state.completeTask,
  completedTasks: state.completedTasks,
  currentPlan: state.currentPlan,
  attempts: state.attempts,
})))

export function useLearningReviewState(plan: GeneratedLearningPlan | null) {
  const source = useStore(useShallow((state) => ({
    studentId: state.profile?.identity.id ?? state.activeStudentId,
    weeklyPlanStartDate: state.weeklyPlanStartDate,
    attempts: state.attempts,
    completedTasks: state.completedTasks,
    taskCompletedAt: state.taskCompletedAt,
  })))

  return useMemo(() => {
    if (!plan || !source.studentId) return null
    return createLearningReview({
      studentId: source.studentId,
      plan,
      weeklyPlanStartDate: source.weeklyPlanStartDate,
      attempts: source.attempts,
      completedTasks: source.completedTasks,
      taskCompletedAt: source.taskCompletedAt,
    })
  }, [plan, source.attempts, source.completedTasks, source.studentId, source.taskCompletedAt, source.weeklyPlanStartDate])
}

export const useClassroomState = () => useStore(useShallow((state) => ({
  student: state.student,
  getEffectiveLayer: state.getEffectiveLayer,
  completeLearningTask: state.completeLearningTask,
  completeLearningRouteTaskAndCommit: state.completeLearningRouteTaskAndCommit,
  completeLesson: state.completeLesson,
  currentPlan: state.currentPlan,
  completedTasks: state.completedTasks,
  voiceEnabled: state.voiceEnabled,
  setVoiceEnabled: state.setVoiceEnabled,
  llmEnabled: state.llmEnabled,
  setLLMConfig: state.setLLMConfig,
  game: state.game,
  startSession: state.startSession,
  recordClassroomAnswerAndCommit: state.recordClassroomAnswerAndCommit,
  usePowerUp: state.usePowerUp,
  getDailyQuest: state.getDailyQuest,
  initializeDailyQuest: state.initializeDailyQuest,
  checkAndRefillEnergy: state.checkAndRefillEnergy,
  clearPendingAchievements: state.clearPendingAchievements,
  pendingAchievements: state.pendingAchievements,
  learningStyle: state.learningStyle,
  recordCheckpoint: state.recordCheckpoint,
  attempts: state.attempts,
})))

export const useCourseBrowserState = () => useStore(useShallow((state) => ({
  completedTasks: state.completedTasks,
  currentPlan: state.currentPlan,
})))

export const usePracticePageState = () => useStore(useShallow((state) => ({
  learnerType: state.learnerType,
  activeStudentId: state.activeStudentId,
  currentPlan: state.currentPlan,
  attempts: state.attempts,
  assessmentRecords: state.assessmentRecords,
  wrongQuestions: state.wrongQuestions,
  srsCards: state.game.personalization.srsCards,
  addAttempt: state.addAttempt,
  completeFormativeTaskSessionAndCommit: state.completeFormativeTaskSessionAndCommit,
  completedTasks: state.completedTasks,
})))

export const useReaderState = () => useStore(useShallow((state) => ({
  activeStudentId: state.activeStudentId,
  currentStage: state.currentStage,
  addLearningEvent: state.addLearningEvent,
})))

export const useLessonPlayerState = () => useStore(useShallow((state) => ({
  completeLessonPlayerAndCommit: state.completeLessonPlayerAndCommit,
  recordLessonPlayerAnswerAndCommit: state.recordLessonPlayerAnswerAndCommit,
  getLessonConfig: state.getLessonConfig,
  getEffectiveLayer: state.getEffectiveLayer,
  stars: state.stars,
  currentPlan: state.currentPlan,
  completedTasks: state.completedTasks,
})))

export const usePretestState = () => useStore(useShallow((state) => ({
  setProfile: state.setProfile,
  setInitialProfile: state.setInitialProfile,
  savedProfile: state.profile,
  activeStudentId: state.activeStudentId,
  activeStudent: state.studentIndex.find((item) => item.id === state.activeStudentId) ?? null,
  initialProfileSessions: state.initialProfileSessions,
  startInitialProfileSession: state.startInitialProfileSession,
  saveInitialProfileSessionPosition: state.saveInitialProfileSessionPosition,
  saveInitialProfileAbilityAnswer: state.saveInitialProfileAbilityAnswer,
  saveInitialProfilePreferenceAnswer: state.saveInitialProfilePreferenceAnswer,
  completeInitialProfileSession: state.completeInitialProfileSession,
})))

export const useSidebarState = () => useStore(useShallow((state) => ({
  stars: state.stars,
})))

export const useStudentGateState = () => useStore(useShallow((state) => ({
  activeStudentId: state.activeStudentId,
  // Keep an in-progress AI remediation recoverable if a workspace commit
  // briefly invalidates the active-student projection in another tab.
  hasAITeacherSession: Boolean(state.aiTeacherSession),
  hasAITeacherActive: state.aiTeacherActive,
  activeProfile: state.activeStudentId ? state.workspaces[state.activeStudentId]?.profile ?? null : null,
  hasPretestResult: Boolean(
    state.activeStudentId && (
      state.workspaces[state.activeStudentId]?.profile
      || state.workspaces[state.activeStudentId]?.pretestLayer
      || state.pretestLayer
      || state.student.layer
    )
  ),
})))

export const useWeeklyPlanState = () => useStore(useShallow((state) => ({
  pretestScore: state.pretestScore,
  weeklyPlanActive: state.weeklyPlanActive,
  weeklyPlanStartDate: state.weeklyPlanStartDate,
  weeklyTaskDone: state.weeklyTaskDone,
  activateWeeklyPlan: state.activateWeeklyPlan,
  toggleWeeklyTask: state.toggleWeeklyTask,
})))

export const useAttemptWriter = () => useStore((state) => state.addAttempt)
export const useAwaitedAttemptWriter = () => useStore((state) => state.addAttemptAndCommit)

export const useQuizBridgeState = () => useStore(useShallow((state) => ({
  completedTasks: state.completedTasks,
  currentPlan: state.currentPlan,
  completeLearningTaskAndCommit: state.completeLearningTaskAndCommit,
  completeLearningRouteTaskAndCommit: state.completeLearningRouteTaskAndCommit,
  awaitWorkspaceCommit: state.awaitWorkspaceCommit,
  getEffectiveLayer: state.getEffectiveLayer,
})))

export function isQuizAttemptPersisted(attemptId: string): boolean {
  return useStore.getState().attempts.some((attempt) => attempt.id === attemptId)
}

export function findQuizAttemptById(attemptId: string): ExerciseAttempt | undefined {
  return useStore.getState().attempts.find((attempt) => attempt.id === attemptId)
}

export function inferQuizRetryCount(exerciseId: string): number {
  return useStore.getState().attempts
    .filter((attempt) => attempt.exerciseId === exerciseId)
    .reduce((nextRetry, attempt) => Math.max(nextRetry, attempt.retryCount + 1), 0)
}

export async function persistQuizAttemptAndCommit(attempt: ExerciseAttempt): Promise<void> {
  await useStore.getState().addAttemptAndCommit(attempt)
}

export async function completeQuizModuleAndCommit(taskId: string): Promise<void> {
  await useStore.getState().completeTaskAndCommit(taskId)
}

export function getCompletedQuizTaskIds(): string[] {
  return Object.keys(useStore.getState().completedTasks)
}
export const useActiveStudentId = () => useStore((state) => state.activeStudentId)
export const useFormativeSessionById = (sessionId: string | null) => useStore((state) => (
  sessionId
    ? state.formativeTaskSessions.find((candidate) => candidate.id === sessionId) ?? null
    : null
))
export const useStartFormativeTaskSession = () => useStore(
  (state) => state.startFormativeTaskSessionAndCommit,
)
export const useSubmitFormativeTaskAnswer = () => useStore(
  (state) => state.submitFormativeTaskAnswerAndCommit,
)

export const useGameSnapshot = () => useStore((state) => state.game)

export const useMarbleDashboardState = () => useStore(useShallow((state) => ({
  student: state.student,
  game: state.game,
  getDailyQuest: state.getDailyQuest,
  initializeDailyQuest: state.initializeDailyQuest,
})))

export const useOutfitState = () => useStore(useShallow((state) => ({
  currentOutfit: state.currentOutfit,
  unlockedOutfits: state.unlockedOutfits,
  ownedOutfits: state.ownedOutfits,
  stars: state.stars,
  learnerType: state.learnerType,
  setOutfit: state.setOutfit,
  purchaseOutfit: state.purchaseOutfit,
})))

export const useAITeacherState = () => useStore(useShallow((state) => ({
  wrongQuestions: state.wrongQuestions,
  aiTeacherResult: state.aiTeacherResult,
  aiTeacherFingerprint: state.aiTeacherFingerprint,
  aiTeacherActive: state.aiTeacherActive,
  aiTeacherSession: state.aiTeacherSession,
  setAITeacherResult: state.setAITeacherResult,
  setAITeacherFingerprint: state.setAITeacherFingerprint,
  setAITeacherActive: state.setAITeacherActive,
  setAITeacherSession: state.setAITeacherSession,
  saveAITeacherAnalysisAndCommit: state.saveAITeacherAnalysisAndCommit,
  clearAITeacher: state.clearAITeacher,
})))

export const usePhaseAdvancementState = () => useStore(useShallow((state) => ({
  phaseAdvancement: state.phaseAdvancement,
  setPhaseAdvancement: state.setPhaseAdvancement,
  setPhaseAdvancementAndCommit: state.setPhaseAdvancementAndCommit,
  clearPhaseAdvancement: state.clearPhaseAdvancement,
  currentPlan: state.currentPlan,
  completedTasks: state.completedTasks,
  assessmentRecords: state.assessmentRecords,
})))

export const useAITeacherSessionState = () => useStore(useShallow((state) => ({
  activeStudentId: state.activeStudentId,
  aiTeacherSession: state.aiTeacherSession,
  saveAITeacherQuizSubmissionAndCommit: state.saveAITeacherQuizSubmissionAndCommit,
  saveAITeacherWorkflowTransitionAndCommit: state.saveAITeacherWorkflowTransitionAndCommit,
  awaitWorkspaceCommit: state.awaitWorkspaceCommit,
  profile: state.profile,
  currentPlan: state.currentPlan,
  currentStage: state.currentStage,
  completedTasks: state.completedTasks,
  assessmentRecords: state.assessmentRecords,
  attempts: state.attempts,
  wrongQuestions: state.wrongQuestions,
  getEffectiveLayer: state.getEffectiveLayer,
  completeAITeacherCertification: state.completeAITeacherCertificationAndCommit,
  setPhaseAdvancement: state.setPhaseAdvancementAndCommit,
})))

export const useWrongQuestionWorkflowActions = () => useStore(useShallow((state) => ({
  addAttemptAndCommit: state.addAttemptAndCommit,
  updateWrongQuestion: state.updateWrongQuestion,
  awaitWorkspaceCommit: state.awaitWorkspaceCommit,
})))

export const useAssessmentRecordState = () => useStore((state) => state.assessmentRecords)

export const useRewardClaims = () => useStore((state) => state.rewardClaims)

export const useRewardClaimReplayState = () => useStore(useShallow((state) => ({
  rewardClaims: Array.isArray(state.rewardClaims) ? state.rewardClaims : [],
  markRewardClaimPlantGrantedAndCommit: state.markRewardClaimPlantGrantedAndCommit,
  recordRewardClaimDeliveryFailureAndCommit: state.recordRewardClaimDeliveryFailureAndCommit,
  completeRewardClaimDeliveryAndCommit: state.completeRewardClaimDeliveryAndCommit,
})))

export const useChallengeState = () => useStore(useShallow((state) => ({
  challengeStats: state.challengeStats,
  updateChallengeSettings: state.updateChallengeSettings,
  resetChallengeStats: state.resetChallengeStats,
  recordChallengeResult: state.recordChallengeResult,
})))

export const useFloatingAssistantState = () => useStore(useShallow((state) => ({
  currentOutfit: state.currentOutfit,
  stars: state.stars,
  completedTasks: state.completedTasks,
  weeklyTaskDone: state.weeklyTaskDone,
  weeklyPlanActive: state.weeklyPlanActive,
  game: state.game,
  getDailyQuest: state.getDailyQuest,
  initializeDailyQuest: state.initializeDailyQuest,
  pretestScore: state.pretestScore,
  student: state.student,
})))
